import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { Children, useEffect, useRef, useState } from 'react';
import { Button } from 'react-bootstrap';
import { useBreakpoints } from '../../../../hooks/useBreakpoints';
import PendingListToolbarSearchToggle from './PendingListToolbarSearchToggle';

const hasVisibleContent = (node) => node != null && node !== false && Children.count(node) > 0;

const countVisibleStats = (node) => Children.toArray(node).filter((child) => child != null && child !== false && child !== true).length;

/** 顶栏：stats 手机横滑；statsOverlay 桌面表头悬浮统计；搜索可叠表头。 */
const PendingListToolbar = ({
	leading,
	stats,
	statsOverlay,
	showSearch = false,
	searchPlaceholder = '搜索',
	statsMobileOnly = false,
	children,
}) => {
	const showLeading = hasVisibleContent(leading);
	const { breakpoints } = useBreakpoints();
	const isMobile = breakpoints.down('md');
	const showStats = hasVisibleContent(stats) && (!statsMobileOnly || isMobile);
	const showStatsOverlay = hasVisibleContent(statsOverlay) && !isMobile;
	const hasChildren = children != null;
	const statsItemCount = countVisibleStats(stats);
	const mainRef = useRef(null);
	const [statsCollapsed, setStatsCollapsed] = useState(false);
	const allowStatsCollapse = showStats && !isMobile;
	const statsCollapsedEffective = allowStatsCollapse && statsCollapsed;
	const useOverlaySearch = showSearch && hasChildren && !isMobile;
	const useHeaderTools = useOverlaySearch || showStatsOverlay;
	const useMobileStatsSearch = isMobile && showSearch && showStats;
	const useMobileToolbarSearch = isMobile && showSearch && !showStats;
	const useListLayout =
		showStats ||
		showStatsOverlay ||
		useOverlaySearch ||
		useMobileStatsSearch ||
		useMobileToolbarSearch ||
		(hasChildren && (showLeading || showSearch));

	useEffect(() => {
		if (!useHeaderTools && !showStats) return undefined;
		const main = mainRef.current;
		if (!main) return undefined;
		const layout = main.closest('.pending-list-layout');

		const syncLayoutMetrics = () => {
			if (useHeaderTools) {
				const th = main.querySelector('thead th');
				if (!th) {
					main.style.removeProperty('--pending-thead-h');
				} else {
					main.style.setProperty('--pending-thead-h', `${th.getBoundingClientRect().height}px`);
				}
			}

			if (showStats && layout && !isMobile) {
				const footer = main.querySelector('.card-footer');
				if (!footer) {
					layout.style.removeProperty('--pending-list-footer-h');
				} else {
					layout.style.setProperty('--pending-list-footer-h', `${footer.getBoundingClientRect().height}px`);
				}
			}
		};

		syncLayoutMetrics();
		const rafId = requestAnimationFrame(syncLayoutMetrics);
		const thead = main.querySelector('thead');
		const footer = main.querySelector('.card-footer');
		const resizeObserver = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(syncLayoutMetrics) : null;
		if (useHeaderTools && thead) resizeObserver?.observe(thead);
		if (showStats && footer) resizeObserver?.observe(footer);
		window.addEventListener('resize', syncLayoutMetrics);

		return () => {
			cancelAnimationFrame(rafId);
			resizeObserver?.disconnect();
			window.removeEventListener('resize', syncLayoutMetrics);
		};
	}, [useHeaderTools, children, statsCollapsed, showStats, isMobile]);

	if (!showLeading && !showStats && !showStatsOverlay && !showSearch && !hasChildren) {
		return null;
	}

	const searchToggle = showSearch ? (
		<PendingListToolbarSearchToggle
			placeholder={useMobileStatsSearch ? '搜索订单、客户…' : searchPlaceholder}
			variant={useOverlaySearch ? 'overlay' : 'toolbar'}
		/>
	) : null;

	if (useListLayout) {
		return (
			<div
				className={classNames('pending-list-layout', {
					'pending-list-layout--stats-collapsed': statsCollapsedEffective,
					'pending-list-layout--no-stats': !showStats,
				})}
			>
				{showStats ? (
					<aside className='pending-list-layout__stats' aria-label='汇总'>
						{statsCollapsedEffective ? (
							<button
								type='button'
								className='pending-list-layout__stats-rail'
								onClick={() => setStatsCollapsed(false)}
								aria-expanded={false}
								aria-label={`展开统计，共${statsItemCount}项`}
								title={`展开统计（${statsItemCount}项）`}
							>
								<span className='pending-list-layout__stats-rail-icon-wrap'>
									<span className='pending-list-layout__stats-rail-icon' aria-hidden>
										<FontAwesomeIcon icon='chart-bar' />
									</span>
									{statsItemCount > 0 ? <span className='pending-list-layout__stats-rail-dot' aria-hidden /> : null}
								</span>
								<span className='pending-list-layout__stats-rail-text'>统计</span>
								<span className='pending-list-layout__stats-rail-chevron' aria-hidden>
									<FontAwesomeIcon icon='chevron-right' />
								</span>
							</button>
						) : (
							<>
								<div className='pending-list-layout__stats-body'>{stats}</div>
								{allowStatsCollapse ? (
									<div className='pending-list-layout__stats-foot'>
										<Button
											type='button'
											variant='link'
											size='sm'
											className='pending-list-layout__stats-toggle text-decoration-none'
											onClick={() => setStatsCollapsed(true)}
											aria-expanded
											aria-label='收起统计'
											title='收起统计'
										>
											<span className='pending-list-layout__stats-toggle-label'>收起</span>
											<FontAwesomeIcon icon='chevron-left' />
										</Button>
									</div>
								) : null}
								{useMobileStatsSearch ? <div className='pending-list-layout__stats-search'>{searchToggle}</div> : null}
							</>
						)}
					</aside>
				) : null}
				<div
					ref={mainRef}
					className={classNames('pending-list-layout__main', {
						'pending-list-layout__main--with-search': useHeaderTools,
					})}
				>
					{useHeaderTools ? (
						<div className='pending-list-layout__search pending-list-layout__header-tools'>
							{showStatsOverlay ? statsOverlay : null}
							{useOverlaySearch ? searchToggle : null}
						</div>
					) : null}
					{useMobileToolbarSearch ? (
						<div className='pending-list-toolbar pending-list-toolbar--in-main pending-list-toolbar--search-only'>
							<div className='pending-list-toolbar__search'>{searchToggle}</div>
						</div>
					) : null}
					{showLeading ? (
						<div className='pending-list-toolbar pending-list-toolbar--in-main'>
							<div className='pending-list-toolbar__leading'>{leading}</div>
						</div>
					) : null}
					{children}
				</div>
			</div>
		);
	}

	const topBar =
		showLeading || showSearch ? (
			<div
				className={classNames('pending-list-toolbar', {
					'pending-list-toolbar--search-only': !showLeading && showSearch,
					'pending-list-toolbar--with-leading': showLeading,
				})}
			>
				{showLeading ? <div className='pending-list-toolbar__leading'>{leading}</div> : null}
				{showSearch ? <div className='pending-list-toolbar__search'>{searchToggle}</div> : null}
			</div>
		) : null;

	return (
		<>
			{topBar}
			{children}
		</>
	);
};

PendingListToolbar.propTypes = {
	leading: PropTypes.node,
	stats: PropTypes.node,
	statsOverlay: PropTypes.node,
	showSearch: PropTypes.bool,
	searchPlaceholder: PropTypes.string,
	statsMobileOnly: PropTypes.bool,
	children: PropTypes.node,
};

export default PendingListToolbar;
